import pdfMake from 'pdfmake/build/pdfmake';

pdfMake.fonts = {
  Roboto: {
    normal: `${window.location.origin}/assets/fonts/pdfmake/Roboto-Regular.ttf`,
    bold: `${window.location.origin}/assets/fonts/pdfmake/Roboto-Medium.ttf`,
    italics: `${window.location.origin}/assets/fonts/pdfmake/Roboto-Italic.ttf`,
    bolditalics: `${window.location.origin}/assets/fonts/pdfmake/Roboto-MediumItalic.ttf`,
  },
};

pdfMake.tableLayouts = {
  simpleTableLayout: {
    hLineWidth: function () {
      return 0.5;
    },
    vLineWidth: function () {
      return 0.5;
    },
    hLineColor: function () {
      return '#696969';
    },
    paddingLeft: function () {
      return 10;
    },
    paddingRight: function () {
      return 10;
    },
  },
};

function printApprovalFormPDF(component): void {
  const formNumberAndStatus = {
    text: [
      { text: `Form #${component.formNumber}`, style: 'header' },
      { text: `  -  ` },
      {
        text: `${component.getFormStatus()}`,
        fontSize: 12,
        italics: true,
        color: '#696969',
      },
    ],
    marginBottom: 20,
  };

  const formAdmin = component.isPACUser()
    ? {
        stack: [
          {
            text: 'Form Admin',
            style: 'sectionHeader',
          },
          {
            columns: [
              {
                width: 'auto',
                stack: [
                  { text: 'Form Owner', style: 'label' },
                  {
                    text: `${component.formOwner.givenName} ${component.formOwner.surname}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: 'auto',
                stack: [
                  { text: 'ITPAC Assignment', style: 'label' },
                  {
                    text: `${component.etsPacAssignment.givenName} ${component.etsPacAssignment.surname}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              !(component.youCompleted() || component.formCompleted())
                ? {
                    width: 'auto',
                    stack: [
                      { text: 'Current Assignment', style: 'label' },
                      {
                        text: `${component.currentAssignment.givenName} ${component.currentAssignment.surname}`,
                        style: 'value',
                        marginTop: 2,
                      },
                    ],
                  }
                : {},
            ],
            columnGap: 30,
            marginTop: 10,
          },
        ],
      }
    : {};

  const notesFromITPAC = () => {
    if (!component.tables || component.tables.length === 0) return {};

    const tableStack = [];

    component.tables.forEach((table) => {
      const tableDefinition = {
        stack: [],
        marginTop: 10,
      };

      tableDefinition.stack.push({
        table: {
          heights: 25,
          body: [
            [
              {
                text: table.tableName,
                style: 'tableLabel',
              },
            ],
          ],
        },
        layout: 'simpleTableLayout',
      });

      const tableBody = [];

      const tableHeaderRowDefinition = table.columns.map((col) => ({
        text: col.colName,
        style: 'tableLabel',
      }));
      tableBody.push(tableHeaderRowDefinition);

      const totalColumns = table.columns.length;

      table.tableData.forEach((row) => {
        for (let i = row.length; i < totalColumns; i++) {
          row.push(''); // Ensure all rows have the same number of columns
        }
        const tableRowDefinition = row.map((cell) => ({
          text: cell,
          style: 'tableValue',
        }));
        tableBody.push(tableRowDefinition);
      });

      tableDefinition.stack.push({
        table: {
          heights: 25,
          body: tableBody,
        },
        layout: 'simpleTableLayout',
        marginTop: 2,
      });

      tableStack.push(tableDefinition);
    });

    return component.tables
      ? {
          stack: [
            {
              text: 'Notes from ITPAC',
              style: 'sectionHeader',
            },
            {
              stack: [
                { text: 'Effective Date', style: 'label' },
                {
                  text: `${
                    component.effectiveDate
                      ? new Date(component.effectiveDate).toLocaleDateString()
                      : 'null'
                  }`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
              marginTop: 10,
            },
            {
              stack: tableStack,
              marginTop: 10,
            },
          ],
          marginTop: 20,
        }
      : {};
  };

  const pageOne = () => {
    const hardwareDetails =
      component.hardwareOrSoftware === 'Hardware Only' ||
      component.hardwareOrSoftware === 'Hardware and Software'
        ? {
            stack: [
              {
                text: 'Hardware Details',
                style: 'label',
              },
              {
                text: `Are you purchasing any service warranties? (If yes, the cost of service warranties should be included in O&M estimate; none of this cost can be capitalized)`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.purchasingWarranties === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
            ],
            marginTop: 15,
          }
        : {};

    const softwareDetails = () => {
      const projectConsistsOfText = component.projectConsistsOfOptions.map((op, i) => ({
        text: [
          { text: op, style: 'longQuestionLabel', bold: false },
          {
            text: `${component.projectConsistsOfCheckboxes[i] ? ' - Yes' : ' - No'}`,
            style: 'binaryValue',
          },
        ],
        marginTop: i === 0 ? 5 : 1,
      }));

      const vendorHostedSection = () =>
        component.isVendorHosted()
          ? [
              {
                text: `Will Duke own perpetual licenses?`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.perpetualLicenses === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
              {
                text: `Does Duke have the contractual right to take possession of the software at any time during the arrangement period?`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.contractualRight === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
              {
                text: `Is there a significant penalty to take possession of the software and bring it in house? (Significant penalty is interpreted as either a significant cost or significant reduction in value or functionality when taking possession of the software. Significant cost is defined as greater than 15% of the total capital cost of the project)`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.significantPenalty === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
              {
                text: `Is it feasible for Duke to run the software on our own
                  hardware or to contract with another third party host?`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.isFeasible === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
              {
                text: `Are there service or usage fees?`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.serviceFees === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
              {
                text: `If we stop paying the service or usage fees, can we still use the software?`,
                marginTop: 10,
                style: 'longQuestionLabel',
              },
              {
                text: `${component.useWithoutPaying === 'YES' ? 'Yes' : 'No'}`,
                style: 'binaryValue',
                marginTop: 2,
              },
            ]
          : [{}];

      return component.hardwareOrSoftware === 'Software Only' ||
        component.hardwareOrSoftware === 'Hardware and Software'
        ? {
            stack: [
              {
                text: 'Software Details',
                style: 'label',
              },
              {
                text: `Work consists of following:`,
                marginTop: 10,
                style: 'label',
              },
              ...projectConsistsOfText,
              {
                stack: [
                  {
                    text: 'What is the expected life of the software (years)?',
                    style: 'label',
                  },
                  {
                    text: `${component.expectedLife}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
                marginTop: 10,
              },
              {
                stack: [
                  {
                    text: `Is this an Agile effort?`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.agileEffort === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    text: `Does the software currently exist or is it being used for any other jurisdictions, department, business unit, or function?`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.softwareBeingUsed === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    columns: [
                      {
                        width: 'auto',
                        stack: [
                          { text: 'Vendor Name(Optional)', style: 'label' },
                          {
                            text: `${component.vendorName ? component.vendorName : 'null'}`,
                            style: 'value',
                            marginTop: 2,
                          },
                        ],
                      },
                      {
                        width: 'auto',
                        stack: [
                          { text: 'Software Name', style: 'label' },
                          {
                            text: `${component.softwareName}`,
                            style: 'value',
                            marginTop: 2,
                          },
                        ],
                      },
                      {
                        width: 'auto',
                        stack: [
                          {
                            text: 'In what year did it go in service?',
                            style: 'label',
                          },
                          {
                            text: `${component.inServiceYear ? component.inServiceYear : 'null'}`,
                            style: 'value',
                            marginTop: 2,
                          },
                        ],
                      },
                    ],
                    columnGap: 30,
                    marginTop: 10,
                  },
                  {
                    text: `Describe the software development work needed for this effort:`,
                    marginTop: 10,
                    style: 'label',
                  },
                  {
                    text: `${component.softwareProjectOverview}`,
                    style: 'value',
                    marginTop: 2,
                  },
                  {
                    text: `Will you have data conversion activities? (If yes, the cost of running processes or manually cleaning up data should be included in O&M estimate; only development of data conversion software can be capitalized)`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.dataConversionActivities === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    text: `Will you have training costs? (If yes, the cost of train-the-trainer and user training should be included in O&M estimate; only developer training on how to develop the software can be capitalized)`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.trainingCosts === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    text: `Is there an associated retirement of existing software?`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.associatedRetirement === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  component.associatedRetirement === 'YES'
                    ? {
                        columns: [
                          {
                            width: 'auto',
                            stack: [
                              {
                                text: 'Software To Be Retired',
                                style: 'label',
                              },
                              {
                                text: `${component.softwareToBeRetired}`,
                                style: 'value',
                                marginTop: 2,
                              },
                            ],
                          },
                          {
                            width: 'auto',
                            stack: [
                              {
                                text: 'When was it in service?',
                                style: 'label',
                              },
                              {
                                text: `${component.retiredInServiceDate}`,
                                style: 'value',
                                marginTop: 2,
                              },
                            ],
                          },
                        ],
                        columnGap: 30,
                        marginTop: 10,
                      }
                    : {},
                  {
                    text: `Will you have any maintenance contracts/agreements?`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.maintenanceContracts === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    text: `Are you replacing any existing interfaces?`,
                    marginTop: 10,
                    style: 'longQuestionLabel',
                  },
                  {
                    text: `${component.replacingInterfaces === 'YES' ? 'Yes' : 'No'}`,
                    style: 'binaryValue',
                    marginTop: 2,
                  },
                  {
                    text: `How will the software be released?`,
                    marginTop: 10,
                    style: 'label',
                  },
                  {
                    text: `${component.howSoftwareReleased}`,
                    style: 'value',
                    marginTop: 2,
                  },
                  ...vendorHostedSection(),
                ],
              },
            ],
            marginTop: 15,
          }
        : {};
    };

    return {
      stack: [
        // Page One Header
        {
          text: 'Page One',
          style: 'sectionHeader',
        },
        // Pati ID
        {
          stack: [
            { text: 'PaTI ID#', style: 'label' },
            {
              text: `${component.displayPatiProject}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 10,
        },
        {
          // Project Details
          columns: [
            {
              width: 'auto',
              stack: [
                { text: 'Short Description', style: 'label' },
                {
                  text: `${component.projectName}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: 'auto',
              stack: [
                { text: 'IT Project Classification', style: 'label' },
                {
                  text: `${component.itProjectClassification.ets_proj_classification}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: 'auto',
              stack: [
                { text: 'Estimated Start Date', style: 'label' },
                {
                  text: `${new Date(component.estimatedStartDate).toLocaleDateString()}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: 'auto',
              stack: [
                { text: 'Estimated In Service Date', style: 'label' },
                {
                  text: `${new Date(component.estimatedInServiceDate).toLocaleDateString()}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          columnGap: 30,
          marginTop: 15,
        },
        {
          // Project Scope
          stack: [
            { text: 'Overview and Scope', style: 'label' },
            {
              text: `${component.projectScopeDescription}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
        },
        {
          // Contacts
          columns: [
            {
              width: 'auto',
              stack: [
                { text: 'Primary Contact', style: 'label' },
                {
                  text: `${component.primaryContact.givenName} ${component.primaryContact.surname} (${component.primaryContact.mail}), ${component.primaryContact.jobTitle}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: 'auto',
              stack: [
                { text: 'Sponsor', style: 'label' },
                {
                  text: `${component.itSponsor.givenName} ${component.itSponsor.surname} (${component.itSponsor.mail}), ${component.itSponsor.jobTitle}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          columnGap: 30,
          marginTop: 15,
        },
        {
          // Request type
          stack: [
            { text: 'Is this request O&M only?', style: 'label' },
            {
              text: `${component.omOnly === 'YES' ? 'Yes' : 'No'}`,
              style: 'binaryValue',
              marginTop: 2,
            },
          ],
          marginTop: 15,
        },
        {
          // Work Relation
          stack: [
            { text: 'Does this work relate to:', style: 'label' },
            {
              text: `${component.relatedWork}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
        },
        hardwareDetails,
        softwareDetails(),
      ],
      marginTop: 20,
    };
  };

  const pageTwo = () => {
    const releaseDetails = () => {
      const content = [];

      component.releaseDetails.forEach((release, i) => {
        release.capabilityDetails.forEach((capability) => {
          content.push({
            columns: [
              {
                width: '10%',
                stack: [
                  {
                    text: 'Release #',
                    style: 'label',
                  },
                  {
                    text: `${capability.releaseNumber}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: '12%',
                stack: [
                  {
                    text: 'Release Date',
                    style: 'label',
                  },
                  {
                    text: `${new Date(capability.releaseDate).toLocaleDateString()}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: '27%',
                stack: [
                  {
                    text: 'Capability',
                    style: 'label',
                  },
                  {
                    text: `${capability.capabilities.slice(0, 35)}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: '27%',
                stack: [
                  {
                    text: 'Capability Description',
                    style: 'label',
                  },
                  {
                    text: `${capability.description.slice(0, 35)}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: '12%',
                stack: [
                  {
                    text: 'Capital Cost',
                    style: 'label',
                  },
                  {
                    text: `${component.currencyPipe.transform(
                      +capability.capitalCost,
                      'USD',
                      'symbol',
                      '1.0-0'
                    )}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
              {
                width: '12%',
                stack: [
                  {
                    text: `O&M Cost`,
                    style: 'label',
                  },
                  {
                    text: `${component.currencyPipe.transform(
                      +capability.omCost,
                      'USD',
                      'symbol',
                      '1.0-0'
                    )}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
            ],
            marginTop: i === 0 ? 10 : 15,
          });
        });
      });

      return !(
        component.hardwareOrSoftware === 'Hardware Only' ||
        component.hardwareOrSoftware === 'O&M Only'
      )
        ? {
            stack: [
              {
                text: 'Capability and Release Details',
                style: 'label',
              },
              ...content,
            ],
            marginTop: 15,
          }
        : {};
    };

    const allocationDetails = {
      stack: [
        {
          text: 'Allocations',
          style: 'label',
          marginTop: 20,
        },
        ...component.allocations.map((allocation, i) => ({
          columns: [
            {
              width: '25%',
              stack: [
                {
                  text: 'Operating Unit',
                  style: 'label',
                },
                {
                  text: `${allocation.displayOperatingUnit || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '25%',
              stack: [
                {
                  text: 'Business Unit',
                  style: 'label',
                },
                {
                  text: `${allocation.businessUnit || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '15%',
              stack: [
                {
                  text: 'Funding Project ID',
                  style: 'label',
                },
                {
                  text: `${
                    allocation.fundingProjectID ? allocation.fundingProjectID.work_order_number : ''
                  }`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '12%',
              stack: [
                {
                  text: 'State',
                  style: 'label',
                },
                {
                  text: `${allocation.state || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '12%',
              stack: [
                {
                  text: 'Function',
                  style: 'label',
                },
                {
                  text: `${allocation.function || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '11%',
              stack: [
                {
                  text: 'Percentage',
                  style: 'label',
                },
                {
                  text: `${allocation.percentage ? allocation.percentage + '%' : ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          marginTop: i === 0 ? 10 : 15,
        })),
        {
          stack: [
            { text: 'Allocation total', style: 'label' },
            {
              text: `${component.allocationTotal()}%`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
          alignment: 'right',
          marginRight: 20,
        },
      ],
      marginTop: 20,
    };

    const materialDetails = {
      stack: [
        {
          text: 'Material Details',
          style: 'label',
          marginTop: 10,
        },
        ...component.buildings.reduce((acc, building, i) => {
          // Add building header
          acc.push({
            columns: [
              {
                width: '100%',
                stack: [
                  {
                    text: 'Building',
                    style: 'label',
                  },
                  {
                    text: `${building.buildingName ? building.buildingName.BuildingName : ''}`,
                    style: 'value',
                    marginTop: 2,
                  },
                ],
              },
            ],
            marginTop: i === 0 ? 10 : 20,
          });

          // Add material estimates
          building.materialEstimates.forEach((estimate, j) => {
            acc.push({
              columns: [
                {
                  width: '15%',
                  stack: [
                    {
                      text: 'Cost',
                      style: 'label',
                    },
                    {
                      text: `${estimate.cost || ''}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
                {
                  width: '20%',
                  stack: [
                    {
                      text: 'Details',
                      style: 'label',
                    },
                    {
                      text: `${estimate.unitOfPropertyDetails || ''}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
                {
                  width: '20%',
                  stack: [
                    {
                      text: 'Description',
                      style: 'label',
                    },
                    {
                      text: `${estimate.unitOfPropertyDescription || ''}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
                {
                  width: '15%',
                  stack: [
                    {
                      text: 'Quantity',
                      style: 'label',
                    },
                    {
                      text: `${estimate.quantity || ''}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
                {
                  width: '15%',
                  stack: [
                    {
                      text: 'Unit Costs',
                      style: 'label',
                    },
                    {
                      text: `${component.currencyPipe.transform(
                        +estimate.unitCosts,
                        'USD',
                        'symbol',
                        '1.0-0'
                      )}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
                {
                  width: '15%',
                  stack: [
                    {
                      text: 'Total Costs',
                      style: 'label',
                    },
                    {
                      text: `${component.currencyPipe.transform(
                        +estimate.totalCosts,
                        'USD',
                        'symbol',
                        '1.0-0'
                      )}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                },
              ],
              marginTop: j === 0 ? 20 : 10,
            });
          });

          return acc;
        }, []),
        {
          stack: [
            { text: 'Total Estimated', style: 'label' },
            {
              text: `${component.currencyPipe.transform(
                component.buildingTotal(),
                'USD',
                'symbol',
                '1.0-0'
              )}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
          alignment: 'right',
          marginRight: 20,
        },
      ],
      marginTop: 20,
    };

    const laborDetails = {
      stack: [
        {
          text: 'Employee/Contractor Labor Details',
          style: 'label',
          marginTop: 10,
        },
        ...component.laborDetails.map((detail, i) => ({
          columns: [
            {
              width: '20%',
              stack: [
                {
                  text: 'Work Task',
                  style: 'label',
                },
                {
                  text: `${detail.workTask ? detail.workTask.workTask : ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '30%',
              stack: [
                {
                  text: 'Work Description',
                  style: 'label',
                },
                {
                  text: `${detail.workDescription || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '20%',
              stack: [
                {
                  text: 'Charge Type',
                  style: 'label',
                },
                {
                  text: `${detail.chargeType || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '20%',
              stack: [
                {
                  text: 'Total Costs',
                  style: 'label',
                },
                {
                  text: `${component.currencyPipe.transform(
                    +detail.totalCosts,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          marginTop: i === 0 ? 10 : 15,
        })),
        {
          stack: [
            { text: 'Total Estimated', style: 'label' },
            {
              text: `${component.currencyPipe.transform(
                component.laborTotal(),
                'USD',
                'symbol',
                '1.0-0'
              )}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
          alignment: 'right',
          marginRight: 20,
        },
      ],
      marginTop: 20,
    };

    const contingencyDetails = {
      stack: [
        {
          text: 'Contingency and Other Details (Optional)',
          style: 'label',
          marginTop: 10,
        },
        ...component.contingencyDetails.map((detail, i) => ({
          columns: [
            {
              width: '25%',
              stack: [
                {
                  text: 'Cost',
                  style: 'label',
                },
                {
                  text: `${detail.cost || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '46%',
              stack: [
                {
                  text: 'Description',
                  style: 'label',
                },
                {
                  text: `${detail.description || ''}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: '25%',
              stack: [
                {
                  text: 'Total Costs',
                  style: 'label',
                },
                {
                  text: `${component.currencyPipe.transform(
                    +detail.totalCosts,
                    'USD',
                    'symbol',
                    '1.0-0'
                  )}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          columnGap: 10,
          marginTop: i === 0 ? 10 : 15,
        })),
        {
          stack: [
            { text: 'Total Estimated', style: 'label' },
            {
              text: `${component.currencyPipe.transform(
                component.contingencyTotal(),
                'USD',
                'symbol',
                '1.0-0'
              )}`,
              style: 'value',
              marginTop: 2,
            },
          ],
          marginTop: 15,
          alignment: 'right',
          marginRight: 20,
        },
      ],
      marginTop: 20,
    };

    const costSummary = {
      stack: [
        {
          text: 'Cost Summary',
          style: 'label',
        },
        {
          table: {
            headerRows: 1,
            widths: ['33%', '33%', '33%'],
            body: [
              [
                { text: 'Total Capital $ by Charge Type', style: 'label' },
                { text: 'Total O&M $ by Charge Type', style: 'label' },
                { text: ' ', style: 'label' },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Internal Labor', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.internalCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Internal Labor', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.internalOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Capital Cost', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'External Labor', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.externalCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'External Labor', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.externalOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Estimated AFUDC of Project', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.estimatedAFUDC(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Material', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.materialCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Material', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.materialOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Total Capital Cost', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.totalCapitalCost(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Contingency', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.contingencyCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Contingency', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.contingencyOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: ' ', style: 'value' },
                        { text: ' ', style: 'value', alignment: 'right' },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Other', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.otherCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Other', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.otherOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: ' ', style: 'value' },
                        { text: ' ', style: 'value', alignment: 'right' },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Subtotal', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalCapital(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Subtotal', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Total O&M Cost', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalOM(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
              [
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Subtotal This Request', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalCapitalThisRequest(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        { text: 'Subtotal This Request', style: 'value' },
                        {
                          text: `${component.currencyPipe.transform(
                            component.subtotalOMThisRequest(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
                {
                  table: {
                    widths: ['50%', '50%'],
                    body: [
                      [
                        {
                          text: 'Total Cost (Including AFUDC)',
                          style: 'value',
                        },
                        {
                          text: `${component.currencyPipe.transform(
                            component.totalProjectCost(),
                            'USD',
                            'symbol',
                            '1.0-0'
                          )}`,
                          style: 'value',
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
          marginTop: 10,
        },
      ],
      marginTop: 20,
    };

    const additionalComments = {
      stack: [
        {
          text: 'Additional Comments to ITPAC (Optional)',
          style: 'label',
        },
        {
          text: component.additionalComments || '',
          style: 'value',
          marginTop: 5,
        },
      ],
      marginTop: 20,
    };

    const attachments = {
      stack: [
        {
          text: 'Attachments',
          style: 'label',
        },
        component.files && component.files.length > 0
          ? {
              table: {
                widths: ['*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'File Name', style: 'tableLabel' },
                    { text: 'Added By', style: 'tableLabel' },
                    { text: 'Date', style: 'tableLabel' },
                  ],
                  ...component.files.map((file) => [
                    { text: file.fileName, style: 'value' },
                    { text: file.addedByName, style: 'value' },
                    { text: file.date, style: 'value' },
                  ]),
                ],
              },
              layout: 'simpleTableLayout',
              marginTop: 5,
            }
          : {
              text: 'No attachments have been added',
              style: 'value',
              marginTop: 5,
            },
      ],
      marginTop: 20,
    };

    const approvers = {
      stack: [
        {
          text: 'Approvers',
          style: 'label',
        },
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              [
                { text: 'Name, Title', style: 'tableLabel' },
                { text: 'DOA Authority Amount', style: 'tableLabel' },
              ],
              [
                {
                  text: `${component.itSponsor.givenName} ${component.itSponsor.surname}, Sponsor`,
                  style: 'value',
                },
                {
                  text: `$${component.doaAmounts[0].toLocaleString('en', {
                    useGrouping: true,
                  })}`,
                  style: 'value',
                },
              ],
              ...component.approverList.map((approver, i) => [
                {
                  text:
                    approver.givenName && approver.surname && approver.jobTitle
                      ? `${approver.givenName} ${approver.surname} (${approver.mail}), ${approver.jobTitle}`
                      : approver.givenName && approver.surname && !approver.jobTitle
                        ? `${approver.givenName} ${approver.surname} (${approver.mail})`
                        : !approver.givenName && !approver.surname && approver.jobTitle
                          ? `${approver.displayName} (${approver.mail}), ${approver.jobTitle}`
                          : `${approver.displayName} (${approver.mail})`,
                  style: 'value',
                },
                {
                  text: `$${component.doaAmounts[i + 1].toLocaleString('en', {
                    useGrouping: true,
                  })}`,
                  style: 'value',
                },
              ]),
            ],
          },
          layout: 'simpleTableLayout',
          marginTop: 5,
        },
      ],
      marginTop: 20,
    };

    return {
      stack: [
        {
          text: 'Page Two',
          style: 'sectionHeader',
        },
        {
          columns: [
            {
              width: 'auto',
              stack: [
                { text: 'Who will this work benefit?', style: 'label' },
                {
                  text: `${component.department.department}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            {
              width: 'auto',
              stack: [
                { text: 'Building Info (Building Name)', style: 'label' },
                {
                  text: `${component.buildingInfo.BuildingName}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
          ],
          columnGap: 30,
          marginTop: 20,
        },
        releaseDetails(),
        {
          columns: [
            {
              width: 'auto',
              stack: [
                {
                  text: 'How do you want to distribute cost across business?',
                  style: 'label',
                },
                {
                  text: `${component.distributeCost}`,
                  style: 'value',
                  marginTop: 2,
                },
              ],
            },
            component.defaultOption
              ? {
                  width: 'auto',
                  stack: [
                    {
                      text: 'Please select one of the default options:',
                      style: 'label',
                    },
                    {
                      text: `${component.defaultOption}`,
                      style: 'value',
                      marginTop: 2,
                    },
                  ],
                }
              : {},
          ],
          columnGap: 30,
          marginTop: 20,
        },
        allocationDetails,
        materialDetails,
        laborDetails,
        contingencyDetails,
        costSummary,
        additionalComments,
        attachments,
        approvers,
      ],
      marginTop: 20,
    };
  };

  const derivationsAndAllocations = {
    stack: [
      {
        text: 'Derivations and Allocations',
        style: 'sectionHeader',
      },
      {
        stack: [
          {
            text: 'Form-Level Derivations',
            style: 'label',
          },
          {
            table: {
              widths: ['auto', 'auto', 'auto'],
              body: [
                [
                  { text: 'Project Manager', style: 'tableLabel' },
                  { text: 'Engineer', style: 'tableLabel' },
                  { text: 'Contract Admin', style: 'tableLabel' },
                ],
                [
                  {
                    text: component.derivationAndAllocations.projectManager || '',
                    style: 'value',
                  },
                  {
                    text: component.derivationAndAllocations.engineer || '',
                    style: 'value',
                  },
                  {
                    text: component.derivationAndAllocations.contractAdmin || '',
                    style: 'value',
                  },
                ],
              ],
            },
            layout: 'simpleTableLayout',
            marginTop: 5,
          },
          {
            text: 'Project Notes:',
            style: 'label',
            marginTop: 20,
          },
          {
            text: component.getProjectNotes() || '',
            style: 'value',
            marginTop: 5,
          },
          {
            text: 'Project Specific Derivations',
            style: 'label',
            marginTop: 20,
          },
          component.itProjects && component.itProjects.length > 0
            ? {
                table: {
                  widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
                  body: [
                    [
                      { text: 'BU', style: 'tableLabel' },
                      { text: 'Project', style: 'tableLabel' },
                      { text: 'Major Location', style: 'tableLabel' },
                      { text: 'Asset Location', style: 'tableLabel' },
                      { text: 'Project Type', style: 'tableLabel' },
                      {
                        text: 'Project Charge Indicator',
                        style: 'tableLabel',
                      },
                      { text: 'Joint Owner Code', style: 'tableLabel' },
                    ],
                    ...component.itProjects.map((project) => [
                      { text: project.businessUnit || '', style: 'value' },
                      { text: project.projectID || '', style: 'value' },
                      { text: project.majorLocation || '', style: 'value' },
                      { text: project.assetLocation || '', style: 'value' },
                      { text: project.projectType || '', style: 'value' },
                      {
                        text: project.projectChargeIndicator || '',
                        style: 'value',
                      },
                      {
                        text:
                          project.projectID && project.projectID.endsWith('C') ? 'CJOBASIC' : '',
                        style: 'value',
                      },
                    ]),
                  ],
                },
                layout: 'simpleTableLayout',
                marginTop: 5,
              }
            : {
                text: 'No project specific derivations available',
                style: 'value',
                marginTop: 5,
              },
          {
            text: 'Activities',
            style: 'label',
            marginTop: 20,
          },
          component.itProjects &&
          component.itProjects.some(
            (project) => project.activities && project.activities.length > 0
          )
            ? {
                table: {
                  widths: ['auto', 'auto', 'auto', 'auto', 'auto'],
                  body: [
                    [
                      { text: 'BU', style: 'tableLabel' },
                      { text: 'Project', style: 'tableLabel' },
                      { text: 'Activity', style: 'tableLabel' },
                      { text: 'Description', style: 'tableLabel' },
                      { text: 'Accounting Model', style: 'tableLabel' },
                    ],
                    ...component.itProjects
                      .filter((project) => project.activities && project.activities.length > 0)
                      .reduce((acc, project) => {
                        project.activities.forEach((activity) => {
                          acc.push([
                            {
                              text: project.businessUnit || '',
                              style: 'value',
                            },
                            { text: project.projectID || '', style: 'value' },
                            { text: activity.activity || '', style: 'value' },
                            {
                              text: activity.activityDescription || '',
                              style: 'value',
                            },
                            {
                              text:
                                activity.accountingModel ||
                                `Activity must be set up manually due to lack of an appropriate acctg model.`,
                              style: 'value',
                            },
                          ]);
                        });
                        return acc;
                      }, []),
                  ],
                },
                layout: 'simpleTableLayout',
                marginTop: 5,
              }
            : {
                text: 'No activities available',
                style: 'value',
                marginTop: 5,
              },
          {
            text: 'Project Estimates',
            style: 'label',
            marginTop: 20,
          },
          ...(component.itProjects && component.itProjects.length > 0
            ? component.itProjects.map((project) => [
                {
                  columns: [
                    {
                      width: 'auto',
                      text: 'BU: ',
                      style: 'label',
                    },
                    {
                      width: 'auto',
                      text: project.businessUnit || '',
                      style: 'value',
                      margin: [5, 0, 20, 0],
                    },
                    {
                      width: 'auto',
                      text: 'Project: ',
                      style: 'label',
                    },
                    {
                      width: 'auto',
                      text: project.projectID || '',
                      style: 'value',
                      margin: [5, 0, 20, 0],
                    },
                    {
                      width: 'auto',
                      text: 'Funding Project: ',
                      style: 'label',
                    },
                    {
                      width: 'auto',
                      text: project.fundingProjectID || '',
                      style: 'value',
                      margin: [5, 0, 20, 0],
                    },
                    {
                      width: 'auto',
                      text: 'Percent: ',
                      style: 'label',
                    },
                    {
                      width: 'auto',
                      text: project.fundingProjectPercent
                        ? `${project.fundingProjectPercent}%`
                        : '',
                      style: 'value',
                    },
                  ],
                  marginTop: 10,
                },
                ...(project.groupedEstimates && project.groupedEstimates.length > 0
                  ? project.groupedEstimates.map((group) => [
                      {
                        columns: [
                          {
                            width: 'auto',
                            text: 'Major Location: ',
                            style: 'label',
                          },
                          {
                            width: 'auto',
                            text: group.majorLocation || '',
                            style: 'value',
                            margin: [5, 0, 20, 0],
                          },
                          {
                            width: 'auto',
                            text: 'Asset Location: ',
                            style: 'label',
                          },
                          {
                            width: 'auto',
                            text: group.assetLocation || '',
                            style: 'value',
                            margin: [5, 0, 0, 0],
                          },
                        ],
                        marginTop: 10,
                      },
                      {
                        table: {
                          widths: ['*', '*', 'auto', 'auto', 'auto'],
                          body: [
                            [
                              {
                                text: 'Utility Account',
                                style: 'tableLabel',
                              },
                              {
                                text: 'Retirement Unit',
                                style: 'tableLabel',
                              },
                              {
                                text: 'Expenditure Type',
                                style: 'tableLabel',
                              },
                              { text: 'Charge Type', style: 'tableLabel' },
                              {
                                text: 'Total Cost',
                                style: 'tableLabel',
                                alignment: 'right',
                              },
                            ],
                            ...group.values.map((estimate) => [
                              {
                                text: estimate.utilityAccountDescription
                                  ? estimate.utilityAccountDescription
                                  : estimate.estimateType.includes('O&M')
                                    ? 'N/A'
                                    : '',
                                style: 'value',
                              },
                              {
                                text: estimate.retirementUnitDescription
                                  ? estimate.retirementUnitDescription
                                  : estimate.estimateType.includes('O&M')
                                    ? 'N/A'
                                    : '',
                                style: 'value',
                              },
                              {
                                text:
                                  estimate.estimateType.includes('CAP') ||
                                  estimate.estimateType.includes('AFUDC')
                                    ? 'Addition'
                                    : estimate.estimateType.includes('Removal')
                                      ? 'Retirement'
                                      : estimate.estimateType.includes('O&M')
                                        ? 'Expense'
                                        : 'N/A',
                                style: 'value',
                              },
                              {
                                text: estimate.estimateType || '',
                                style: 'value',
                              },
                              {
                                text: `$${estimate.dollarAmount.toLocaleString('en', {
                                  useGrouping: true,
                                })}`,
                                style: 'value',
                                alignment: 'right',
                              },
                            ]),
                          ],
                        },
                        layout: 'simpleTableLayout',
                        marginTop: 10,
                      },
                      {
                        columns: [
                          {
                            width: '*',
                            text: 'Location Total:',
                            style: 'label',
                            alignment: 'right',
                          },
                          {
                            width: 'auto',
                            text: `$${group.locationCost.toLocaleString('en', {
                              useGrouping: true,
                            })}`,
                            style: 'value',
                            alignment: 'right',
                            margin: [5, 0, 0, 0],
                          },
                        ],
                        marginTop: 5,
                      },
                    ])
                  : []),
                {
                  columns: [
                    {
                      width: '*',
                      text: 'Project Total:',
                      style: 'label',
                      alignment: 'right',
                    },
                    {
                      width: 'auto',
                      text: project.totalProjectCost
                        ? `$${project.totalProjectCost.toLocaleString('en', {
                            useGrouping: true,
                          })}`
                        : '$0',
                      style: 'value',
                      alignment: 'right',
                      margin: [5, 0, 0, 0],
                    },
                  ],
                  marginTop: 10,
                },
              ])
            : [
                {
                  text: 'No project estimates available',
                  style: 'value',
                  marginTop: 5,
                },
              ]),
          {
            columns: [
              {
                width: '*',
                text: 'Grand Total:',
                style: 'label',
                alignment: 'right',
              },
              {
                width: 'auto',
                text: `$${component.getGrandTotalCost().toLocaleString('en', {
                  useGrouping: true,
                })}`,
                style: 'value',
                alignment: 'right',
                bold: true,
                margin: [5, 0, 0, 0],
              },
            ],
            marginTop: 10,
          },
        ],
        marginTop: 20,
      },
    ],
    marginTop: 20,
  };

  const formActivity = {
    stack: [
      {
        text: 'Form Activity',
        style: 'sectionHeader',
      },
      {
        table: {
          headerRows: 0,
          widths: ['50%', '50%'],
          body: component.formActivity.map((activity) => {
            let actionColor = '#4c4c4c'; // Default color
            if (activity.action === 'Approved') {
              actionColor = '#00853f'; // Green for approved
            } else if (activity.action === 'Rejected') {
              actionColor = '#9e1b32'; // Red for rejected
            }

            let statusText = activity.status;
            // Convert specific status labels to display versions
            if (activity.status === 'ET&S PAC Review') {
              statusText = 'ITPAC Review';
            } else if (activity.status === 'Lead ET&S PAC Review') {
              statusText = 'Lead ITPAC Review';
            } else if (activity.status === 'IT Sponsor Review') {
              statusText = 'Sponsor Review';
            } else if (activity.status && activity.status.startsWith('DOA Review')) {
              statusText = 'DOA Review';
            }

            return [
              {
                stack: [
                  {
                    columns: [
                      {
                        text: activity.name,
                        color: '#4c4c4c',
                      },
                      {
                        text: activity.action,
                        color: actionColor,
                        alignment: 'right',
                      },
                    ],
                    marginBottom: 5,
                  },
                  {
                    text: statusText
                      ? `${statusText} - ${activity.time}, ${activity.day}`
                      : `${activity.time}, ${activity.day}`,
                    color: '#696969',
                    fontSize: 10,
                    marginBottom: 5,
                  },
                  {
                    text: activity.comment || '',
                    color: '#4c4c4c',
                    fontSize: 10,
                    marginBottom: 5,
                  },
                ],
                fillColor: '#ffffff',
                margin: [5, 5, 5, 5],
              },
            ];
          }),
        },
        layout: {
          hLineWidth: function (i, node) {
            return 1;
          },
          vLineWidth: function (i, node) {
            return 1;
          },
          hLineColor: function (i, node) {
            return '#e0e0e0';
          },
          vLineColor: function (i, node) {
            return '#e0e0e0';
          },
          paddingLeft: function (i, node) {
            return 10;
          },
          paddingRight: function (i, node) {
            return 10;
          },
          paddingTop: function (i, node) {
            return 5;
          },
          paddingBottom: function (i, node) {
            return 5;
          },
        },
        marginTop: 20,
      },
    ],
    marginTop: 20,
  };

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [20, 15],
    content: [
      formNumberAndStatus,
      formAdmin,
      notesFromITPAC(),
      pageOne(),
      pageTwo(),
      derivationsAndAllocations,
      formActivity,
    ],
    footer: function (currentPage, pageCount) {
      return {
        columns: [
          { text: 'Page ' + currentPage.toString() + ' of ' + pageCount, alignment: 'right' },
        ],
        margin: [40, 0, 40, 0], // Adjust margins as needed
      };
    },
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 10],
        color: '#005984',
      },
      label: {
        fontSize: 12,
        color: '#696969',
        bold: true,
      },
      value: {
        fontSize: 12,
        color: '#696969',
        italics: true,
      },
      sectionHeader: {
        color: '#005984',
        fontSize: 14,
        bold: true,
      },
      tableLabel: {
        fontSize: 12,
        color: '#696969',
        bold: true,
        align: 'center',
        marginTop: 5,
      },
      tableValue: {
        fontSize: 12,
        color: '#696969',
        align: 'center',
        marginTop: 5,
        italics: true,
      },
      longQuestionLabel: {
        fontSize: 10,
        color: '#696969',
      },
      binaryValue: {
        fontSize: 12,
        color: '#696969',
        italics: true,
        bold: true,
      },
    },
  };

  pdfMake.createPdf(docDefinition).print();
}

export { printApprovalFormPDF };
